import isNil from "lodash/isNil";
import { CSSProperties } from "react";
import { useForm } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { radius } from "#app/theme/v1/radius";
import { spacing } from "#app/theme/v1/spacing";
import { useClients } from "#modules/clients/view/useClients";
import { useMaterialGradeDetails } from "#modules/materials/view/useMaterialGradeDetails";
import { getPropertyOptions } from "#modules/similarPart/domain/getPropertyOptions";
import { ValueType } from "#modules/similarPart/view/filters/FilterPropertyInputController";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Flex } from "#shared/view/components/Flex";
import { Text } from "#shared/view/components/Text";
import { FilterQuery } from "shared";

type Props = {
  filtersParams: FilterQuery;
  hasDimensionsFilter: boolean;
  removeFilter: (params: FilterQuery) => void;
  refetch: () => void;
};

export const SelectedFilters = ({
  filtersParams,
  hasDimensionsFilter,
  removeFilter,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const { filterGroup } = filtersParams;
  const { expressions } = filterGroup;

  const formMethods = useForm<FilterQuery>({
    defaultValues: filtersParams,
  });

  const { control } = formMethods;

  const { remove } = useFieldArray({
    control,
    name: "filterGroup.expressions",
  });

  const { clients } = useClients();
  const { materialGradeDetails } = useMaterialGradeDetails();

  const getReadableValue = ({
    type,
    value,
  }: {
    type: ValueType;
    value: string | number | boolean | null;
  }) => {
    if (type === "materialGrade") {
      const materialGrade = materialGradeDetails.find((grade) => grade.materialGrade === value);

      return materialGrade?.name;
    }

    if (type === "client") {
      const client = clients.find((c) => c.id === value);

      return client?.name;
    }

    if (type === "size") {
      return `${value ?? "-"} mm`;
    }

    if (type === "number") {
      return value;
    }

    if (type === "percentage") {
      return `${value ?? "-"}%`;
    }
    if (type === "feature") {
      if (value === "Folding") {
        return "des plis";
      } else if (value === "Hole") {
        return "des trous";
      }

      return value;
    }

    return value;
  };

  const propertyOptions = getPropertyOptions({ hasDimensionsFilter });

  const onRemoveFilter = (index: number) => {
    remove(index);
    removeFilter({
      ...filtersParams,
      filterGroup: {
        ...filtersParams.filterGroup,
        expressions: expressions.filter((_, i) => i !== index),
      },
    });
    refetch();
  };

  return (
    <Flex wrap={"wrap"}>
      {expressions.map((expression, index) => {
        const property = propertyOptions.find((p) => p.value === expression.property);

        if (!property || isNil(expression.value) || expression.value === "") {
          return null;
        }

        return (
          <Flex
            key={`${property.label}-${expression.operator}-${expression.value}`}
            style={styles.expression}
          >
            <ActionIcon mr="xxs" onClick={() => onRemoveFilter(index)}>
              <Icon name="x" />
            </ActionIcon>

            <Text style={styles.selector}> {property.label}</Text>
            <Text style={styles.operator}>{t(`filterOperators.${expression.operator}`)}</Text>
            <Text style={styles.selector}>
              {getReadableValue({
                type: property.type,
                value: expression.value,
              })}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const styles: Record<string, CSSProperties> = {
  expression: {
    display: "flex",
    alignItems: "center",
    marginRight: spacing.xs,
    background: colors.blue[0],
    marginBottom: spacing.xxxs,
    paddingLeft: spacing.xxxs,
    borderRadius: radius.sm,
  },
  selector: {
    fontWeight: "bold",
    marginRight: spacing.xxxs,
  },
  operator: {
    color: colors.gray[6],
    fontSize: fontSizes.small,
    marginRight: spacing.xxxs,
  },
};
